/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import MediaPrepHeaderSection from '../../../common/commonComponents/mediaPrepheaderSection';
import MediaPrepPageHeader from '../../../common/commonComponents/mediaPrepPageHeader';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGE_SIZE } from '../../../../constants/constants';
import { MediaSolutionProductMasterService } from '../../../../service/mediaSolutionProductMaster';
import { getSolutionProductMasterColumns } from './partials/column';
import MediaPreparationTable from '../../../common/commonComponents/mediaPreparationTable';
import ProductMasterFilterBar from "../media_powder_product_master/partials/filterBar";
import Pagination from '../../../../widgets/pagination';
import AddSolutionProduct from "./views/AddSolutionProduct";
// import SolutionProductDetails from "./views/SolutionProductDetails";
import CustomToast from "../../../../widgets/toaster";
import SolutionProductDetails from './views/SolutionProductDetails';
import { userService } from '../../../../service/userService';
import AlertModal from '../../../../widgets/alertModal';


interface SolutionProductMasterProps {
  onAddProduct?: () => void;
  onViewProduct?: (id: string) => void;
}

const SolutionProductMaster: React.FC<SolutionProductMasterProps> = ({ onAddProduct, onViewProduct }) => {
  const [solutions, setSolutions] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [manufacturerFilter, setManufacturerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categories, setCategories] = useState<any[]>([]);
  const { t } = useTranslation();
  const [view, setView] = useState<"list" | "create" | "details">("list");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [loading, setLoading] = useState(false);
 
  const [showStatusAlert, setShowStatusAlert] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState({
      id: "",
      status: "",
    });
  
    const handleStatusAlert = (id: string, status: string) => {
      setSelectedStatus({
        id,
        status,
      });
      setShowStatusAlert(true);
    };

  const handleStatusChange = async () => {
    const updatedStatus =
      selectedStatus.status === "active" ? "inactive" : "active";

    const payload = {
      id: selectedStatus.id,
      status: updatedStatus,
      interface_name: "Update Solution Product Master",
    };

    try {
      const response =
        await MediaSolutionProductMasterService.changeSolutionProductStatus(
          payload
        );

      if (response.status && response.statusCode === 200) {
        getSolutionProductList();
        setToastMessage(response.message);
        setToastType("success");
      } else {
        setToastMessage(response.message);
        setToastType("error");
      }

      setShowToast(true);
      setShowStatusAlert(false);
    } catch (error) {
      console.error(error);
      setShowStatusAlert(false);
    }
  };  

  const [sort, setSort] = useState("id");
    const [order, setOrder] = useState("desc");
    const handleSort = (key: string, value: boolean) => {
      setSort(key);
      setOrder(value ? "asc" : "desc");
      setCurrentPage(1);
    };

  const [permission, setPermission] = useState<any>([]);
	const getPermissionByRole = async (role: number) => {
		try {
		const response = await userService.getPermissionByRole(role);

		if (response.status && response.statusCode === 200) {
			setPermission(response.data.permissions);
		}
		} catch (error) {
		console.error(error);
		}
	};

	useEffect(() => {
		const storedData = localStorage.getItem("userDetails");

		if (storedData) {
		const storedObject = JSON.parse(storedData);
		getPermissionByRole(storedObject[0].role_master_id);
		}
	}, []);


  // Fetch product master list with optional filters
  const getSolutionProductList = async () => {
    setLoading(true);
    try {
      const response =
        await MediaSolutionProductMasterService.getSolutionProductDefinitions(
          currentPage,
          pageSize,
          sort,
          order, 
          searchTerm,
          categoryFilter,
          statusFilter
        );

      if (response.status && response.statusCode === 200) {
        const mappedData = response.data.map((item: any) => ({
          id: item.id,
          name: item.solution_product_name,  
          category: item.category_name,
          mf: item.manufacturer_name,
          cat: item.catalog_number,
          shelfLife: item.shelf_life
            ? `${item.shelf_life} Days`
            : "-",
          status: item.status,  
        }));

        setSolutions(mappedData);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } else {
        setSolutions([]);
      }
    } catch (error) {
      console.error("Solution Product API error:", error);
    }
    finally {
      setLoading(false);
    }
  };

  
  // Fetch category list 
  const getCategories = async () => {
    try {
      const response = await MediaSolutionProductMasterService.getCategoryList();

      if (response.status && response.statusCode === 200) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error("Category API error:", error);
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);

    if (size) {
      setPageSize(size);
    }
  };

  const showToastMessage = (message: string, type: string) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  useEffect(() => {
    getSolutionProductList();
    getCategories();
  }, [currentPage, pageSize, sort, order, searchTerm, categoryFilter, statusFilter]);

  if (view === "create") {
    return (
      <>
        <CustomToast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={2500}
          message={toastMessage}
          toastType={toastType}
        />

        <AddSolutionProduct
          showToastMessage={showToastMessage}
          onCancel={() => {
            setView("list");
            getSolutionProductList();
          }}
          onSave={() => {
            setView("list");
            getSolutionProductList();
          }}
        />
      </>
    );
  }

  if (view === "details" && selectedProductId) {
    return (
      <SolutionProductDetails
        productId={selectedProductId}
        onBack={() => {
          setView("list");
          getSolutionProductList();
        }}
      />
    );
  }

  return (
    <div>
    <CustomToast
      show={showToast}
      onClose={() => setShowToast(false)}
      delay={2500}
      message={toastMessage}
      toastType={toastType}
    />
    <MediaPrepHeaderSection />
    <MediaPrepPageHeader pageHeader={t('mediaSolutionProductMaster.title')}  pageDescription={t('pageDescription.mediaSolutionProductMaster.description')} />
      <div className="media-prep-scope px-8 pb-13 pt-3 max-w-full animate-in fade-in duration-500 min-h-full">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-2">
        <div className="space-y-1">
            {/* <h2 className='headText1 m-0 d-flex align-items-center' style={{ fontSize: '18px', height: '100%' }}>
            {t('mediaSolutionProductMaster.title')}
            </h2>
            <p className="text-slate-400 text-[12.5px] font-medium">
            {t('mediaSolutionProductMaster.description')}
            </p> */}
        </div>
        <div className="ml-[360px] mt-2">
          {permission?.includes("add_media_solution_product") && (
          <button
            onClick={() => setView("create")}
            className="bg-[#FF7043] text-white flex items-center gap-1 px-4 py-2 rounded-lg shadow-[0_8px_20px_rgba(255,112,67,0.2)] font-semibold text-xs uppercase tracking-wide whitespace-nowrap hover:brightness-110 active:scale-95 transition-all border-none"
          >
            <span className="material-symbols-outlined text-[23px]">{t('mediaPowder.icons.add_box')}</span>
            {t('mediaSolutionProductMaster.actions.AddSolutionProduct')}
          </button>
          )}
        </div>
        </div>

      
      <ProductMasterFilterBar
        mediaTypes={categories} 
        selectedMediaType={categoryFilter}
        searchTerm={searchInput}
        totalElements={totalElements}
        manufacturerFilter={manufacturerFilter}
        statusFilter={statusFilter}
        onManufacturerChange={setManufacturerFilter}
        onStatusChange={setStatusFilter}
        valueKey="id"     
        labelKey="name" 

        onSearchChange={(val) => {
          setSearchInput(val);

          if (!val) {
            setSearchTerm("");
            setCurrentPage(1);
          }
        }}

        onMediaTypeChange={(val) => {
          setCategoryFilter(val);
          setCurrentPage(1);
        }}

        onClearFilters={() => {
          setCategoryFilter("");
          setSearchTerm("");
          setSearchInput("");
          setCurrentPage(1);
          setManufacturerFilter("");
          setStatusFilter("");
        }}

        onSearchClick={() => {
          setSearchTerm(searchInput);
          setCurrentPage(1);
        }}

        filterLabel={t('mediaSolutionProductMaster.filter_list.category')}  
        filterPlaceholder={t('mediaSolutionProductMaster.filter_list.allCategories')}
      />

      <MediaPreparationTable
        columns={getSolutionProductMasterColumns(
          t,
          handleStatusAlert,
          permission?.includes("edit_solution_product")
        )}
        data={solutions}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        onSortClick={handleSort}
        renderAction={(row) => (
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setSelectedProductId(row.id);
                setView("details");
              }}
              className="flex items-center justify-center gap-1 w-[90px] h-[26px] bg-[rgb(54,64,93)] text-white rounded-lg font-black text-[8px] uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-sm border-none outline-none focus:outline-none"
            >
              <span className="material-symbols-outlined text-base">
                {t('mediaPowder.icons.visibility')}
              </span>
              {t('mediaSolutionProductMaster.actions.view')}
            </button>
          </div>
        )}
      />
    </div>
    <Pagination
      page={currentPage}
      totalPages={totalPages}
      handlePageChange={handlePageChange}
    />
    <AlertModal
      show={showStatusAlert}
      onHide={() => setShowStatusAlert(false)}
      title={t("common.alert")}
      message="Do you want to change the status?"
      onConfirm={handleStatusChange}
    />
   </div>
  );
};

export default SolutionProductMaster;
